import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import Image from '@base/Image';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import {
  APP_CONTAINER_WIDTH,
  SPACING_2B,
  SPACING_4B,
  SPACING_7B,
  SPACING_11B
} from '@styles/constants/spacings';
import DangerousP2 from '@unicons-base/DangerousP2/DangerousP2';
import Typography from '@unicons-base/Typography/Typography';
import UN_ICONS_TYPOGRAPHY_VARIANT from '@unicons-types/typography.type';
import AnyfeedButton from '@unicons-comp/AnyfeedButton/AnyfeedButton';
import List from '@unicons-comp/List/List';
import { DataType, ITCL_SUBDATA_TYPES } from '@unicons-types/anyfeed.type';
import isEmptyObject from '@utils/isEmptyObject';
import { emptyObject } from '@constants/empty';
import WatchTrailer from '@unicons-comp/Course/WatchTrailer/WatchTrailer';
import useWindowSize from '@hooks/useWindowSize';
import { useLessonPlayer } from '@stores/lessonPlayerStore';
import AnyfeedIconCard from './AnyfeedIconCard';

type WrapperType = {
  $isHeader: boolean;
};
interface AnyfeedITCLProps {
  data: DataType;
  list: any[];
  isHeader?: boolean;
}

const Wrapper = styled.div<WrapperType>`
  position: ${({ $isHeader }) => !$isHeader && 'relative'};
  display: flex;
  max-width: ${APP_CONTAINER_WIDTH};
  justify-content: space-between;
  flex-direction: row;
  margin: 0 auto;
  background: ${({ $isHeader }) =>
    !$isHeader && 'var(--color-i-icon-dark-primary)'};
  border-radius: ${({ $isHeader }) => !$isHeader && '8px'};
  padding: ${({ $isHeader }) =>
    $isHeader
      ? `${Spacings.SPACING_25B} 0`
      : `${Spacings.SPACING_14B} ${Spacings.SPACING_12B}`};
  ${mediaQuery.uptoIconTablet} {
    padding: ${({ $isHeader }) =>
      $isHeader ? SPACING_4B : `0px ${SPACING_4B}`};
    max-width: 100%;
    margin: 0 auto;
    justify-content: center;
    align-items: center;
    flex-direction: column-reverse;
    background: none;
    margin-bottom: ${({ $isHeader }) =>
      $isHeader ? Spacings.SPACING_6B : '0px'};
  }
  ${mediaQuery.uptoMobile} {
    padding: ${({ $isHeader }) => ($isHeader ? `${SPACING_4B}` : '0px')};
    margin-bottom: ${({ $isHeader }) =>
      $isHeader ? Spacings.SPACING_6B : '0px'};
  }
`;

const StyledPlayerIcon = styled.div`
  position: absolute;
  left: 0px;
  right: 16px;
  top: 0px;
  bottom: 0px;
  width: 56px;
  height: 56px;
  margin: auto;
  cursor: pointer;
  transition: transform 400ms ease-in-out;
  ${mediaQuery.uptoMobile} {
    height: ${Spacings.SPACING_10B};
    width: ${Spacings.SPACING_10B};
    bottom: 8px;
    left: 8px;
  }
`;

const StyledCoverWrapper = styled.div<{
  $isHeader: boolean;
  $isIconCard: boolean;
}>`
  position: ${({ $isHeader }) => ($isHeader ? 'relative' : 'absolute')};
  width: ${({ $isHeader, $isIconCard }) => {
    if ($isIconCard) return '211px';
    return $isHeader ? '570px' : '100%';
  }};
  min-width: ${({ $isHeader }) => $isHeader && '570px'};
  height: ${({ $isHeader, $isIconCard }) => {
    if ($isIconCard) return '268px';
    return $isHeader ? '360px' : '100%';
  }};
  border-radius: ${({ $isHeader }) => ($isHeader ? '8px' : '0px')};
  overflow: hidden;
  bottom: ${({ $isIconCard }) => $isIconCard && '0px'};
  top: ${({ $isIconCard }) => ($isIconCard ? 'unset' : '0')};
  right: ${({ $isIconCard }) => ($isIconCard ? '55px' : '0')};
  max-width: 504px;
  cursor: ${({ $isHeader }) => $isHeader && 'pointer'};
  & img {
    object-fit: cover;
  }
  &:hover ${StyledPlayerIcon} {
    transform: scale(1.1);
  }
  ${mediaQuery.uptoIconTablet} {
    position: relative;
    width: ${({ $isHeader }) => ($isHeader ? '328px' : '100%')};
    height: ${({ $isHeader }) => ($isHeader ? '324px' : '260px')};
    max-width: 100%;
    margin-bottom: ${Spacings.SPACING_8B};
  }
  ${mediaQuery.uptoMobile} {
    width: ${({ $isHeader }) => ($isHeader ? '100%' : '100%')};
    height: ${({ $isHeader }) => ($isHeader ? '188px' : '170px')};
    margin-bottom: ${Spacings.SPACING_6B};
    min-width: ${({ $isHeader }) => $isHeader && '328px'};
  }
`;

const StyledHeaderContent = styled.div<WrapperType>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  ${mediaQuery.uptoMobile} {
    width: 100%;
    padding: ${({ $isHeader }) =>
      $isHeader ? '0px' : `0 ${Spacings.SPACING_4B}`};
  }
`;

const StyledButton = styled(AnyfeedButton)<WrapperType>`
  margin-top: ${({ $isHeader }) => ($isHeader ? SPACING_11B : SPACING_7B)};
  width: ${({ $isHeader }) => ($isHeader ? '234px' : '200px')};
  font-size: ${({ $isHeader }) => ($isHeader ? Fonts.LARGE : '')};

  ${mediaQuery.uptoIconTablet} {
    width: 328px;
    margin: auto;
    margin-top: ${({ $isHeader }) =>
      $isHeader ? Spacings.SPACING_6B : SPACING_2B};
  }
  ${mediaQuery.uptoMobile} {
    font-size: ${({ $isHeader }) => ($isHeader ? Fonts.LARGE : '')};
    width: 100%;
    max-width: 100%;
    margin-top: ${Spacings.SPACING_6B};
  }
`;

const StyledTitle = styled(Typography)<WrapperType>`
  max-width: ${({ $isHeader }) => ($isHeader ? '400px' : '510px')};
  background: ${({ $isHeader }) =>
    $isHeader
      ? 'linear-gradient(261.69deg, var(--color-i-icon-gradient-1) 0%, var(--color-i-icon-gradient-2) 69.95%, var(--color-i-icon-gradient-3) 100%)'
      : 'var(--color-i-icon-light-tertiary)'};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  &::selection {
    -webkit-text-fill-color: var(--color-i-all-black);
  }
  ${mediaQuery.uptoIconTablet} {
    font-size: ${({ $isHeader }) => ($isHeader ? '40px' : Fonts.H2)};
    max-width: 570px;
    text-align: center;
    line-height: ${({ $isHeader }) =>
      $isHeader ? Spacings.SPACING_14B : '38px'};
  }

  ${mediaQuery.uptoMobile} {
    font-size: ${({ $isHeader }) => ($isHeader ? Fonts.H2 : Fonts.H3)};
    margin-bottom: ${({ $isHeader }) =>
      $isHeader ? Spacings.SPACING_2B : Spacings.SPACING_4B};
    margin-left: auto;
    margin-right: auto;
    text-align: center;
    max-width: 320px;
    line-height: ${({ $isHeader }) =>
      $isHeader ? '38px' : Spacings.SPACING_7B};
  }
`;

const StyledDescription = styled(Typography)<WrapperType>`
  max-width: 510px;
  font-weight: ${Fonts.REGULAR};
  ${mediaQuery.uptoIconTablet} {
    max-width: 570px;
    margin-left: auto;
    margin-right: auto;
    text-align: center;
  }
  ${mediaQuery.uptoMobile} {
    margin-bottom: 0;
    max-width: 307px;
  }
`;

const StyledHeaderLeft = styled.img`
  position: absolute;
  top: 149px;
  transform: scale(0.8);
  left: -18px;
  opacity: 0.3;
  display: flex;
  ${mediaQuery.uptoIconTablet} {
    display: none;
  }
`;

const StyledHeaderRight = styled.img`
  position: absolute;
  top: -183px;
  right: 0px;
  opacity: 0.3;
  display: flex;
  ${mediaQuery.uptoIconTablet} {
    display: none;
  }
`;

const StyledTitleCover = styled.div`
  position: relative;
  width: 330px;
  height: 84px;
`;

const StyledSubText = styled(DangerousP2)`
  ${mediaQuery.uptoIconTablet} {
    margin-left: auto;
    margin-right: auto;
  }
`;
const AnyfeedITCL: React.FC<AnyfeedITCLProps> = ({ data, list, isHeader }) => {
  const [isTrailerVisible, setTrailer] = useState(false);
  const setFullscreen = useLessonPlayer.use.setFullscreen();
  const { subdataType } = data || emptyObject;

  const {
    cta,
    image,
    video,
    listData,
    title: heading
  } = list?.[0] || emptyObject;

  const { width } = useWindowSize();

  const handleClose = () => {
    setTrailer(false);
    setFullscreen(false);
  };
  const handleOpen = () => {
    if (video?.video) {
      setTrailer(true);
      setFullscreen(true);
    }
  };

  const isIconCard = useMemo(
    () => subdataType === ITCL_SUBDATA_TYPES.ICON_CARD,
    [subdataType]
  );

  const mediaSrc = useMemo(() => {
    if (!isHeader && width < 1081 && !isIconCard)
      return 'https://icons.uacdn.net/commons/im/d10e0a20-1596-4026-9b78-7cd554c66c80.png';
    if (!isHeader && !isIconCard) return image?.image;
    return image?.image || video?.thumbnail;
  }, [isHeader, image, video, width, isIconCard]);

  const titleVariant = useMemo(
    () =>
      isHeader
        ? UN_ICONS_TYPOGRAPHY_VARIANT.H1
        : UN_ICONS_TYPOGRAPHY_VARIANT.H2,
    [isHeader]
  );
  const descriptionVariant = useMemo(
    () =>
      isHeader
        ? UN_ICONS_TYPOGRAPHY_VARIANT.SUBTITLE1
        : UN_ICONS_TYPOGRAPHY_VARIANT.SUBTITLE2,
    [isHeader]
  );

  if (isIconCard) {
    return (
      <AnyfeedIconCard
        heading={heading}
        titleVariant={titleVariant}
        cta={cta}
        image={image}
      />
    );
  }
  return (
    <Wrapper $isHeader={isHeader}>
      {isHeader && (
        <StyledHeaderLeft
          src="/static/images/anyfeed-header-1.svg"
          alt="Unacademy Icons"
          width="239px"
          height="457px"
        />
      )}

      {isHeader && (
        <StyledHeaderRight
          src="/static/images/anyfeed-header-2.svg"
          alt="Unacademy Icons"
          width="246px"
          height="490px"
        />
      )}

      <StyledHeaderContent $isHeader={isHeader}>
        {heading?.titleImage && (
          <StyledTitleCover>
            <Image
              src={heading?.titleImage}
              alt="Unacademy Icons"
              layout="fill"
            />
          </StyledTitleCover>
        )}
        {heading?.title && (
          <StyledTitle
            $isHeader={isHeader}
            variant={titleVariant}
            color={heading?.titleColor || 'var(--color-i-icon-light-tertiary)'}
          >
            {heading.title}
          </StyledTitle>
        )}
        {heading?.description && (
          <StyledDescription
            $isHeader={isHeader}
            variant={descriptionVariant}
            color={
              heading?.descriptionColor || 'var(--color-i-icon-dark-secondary)'
            }
          >
            {heading.description}
          </StyledDescription>
        )}
        {listData && !isEmptyObject(listData) && <List list={listData.items} />}
        {cta && (
          <StyledButton
            $isHeader={isHeader}
            title={cta?.title}
            titleColor={cta?.titleColor}
            backgroundColor={cta?.backgroundColor}
            isSubscriptionCta={cta?.category === 'subscription'}
            isCourseCta={cta?.category === 'course'}
            ctaMetaData={cta?.metadata}
          />
        )}
        {cta?.description && <StyledSubText paragraph={cta?.description} />}
      </StyledHeaderContent>
      {(image?.image || video?.thumbnail) && (
        <StyledCoverWrapper
          $isIconCard={isIconCard}
          $isHeader={isHeader}
          onClick={handleOpen}
        >
          <Image
            src={mediaSrc}
            alt={
              video?.thumbnail
                ? 'Unacademy Icons - Sachin Tendulkar'
                : heading?.title || 'Unacademy Icons'
            }
            layout="fill"
          />
          {video?.thumbnail && (
            <StyledPlayerIcon>
              <Image src="play-white-circle.svg" alt="Lesson" layout="fill" />
            </StyledPlayerIcon>
          )}
        </StyledCoverWrapper>
      )}
      {video?.video && (
        <WatchTrailer
          src={video?.video}
          open={isTrailerVisible}
          handleClose={handleClose}
        />
      )}
    </Wrapper>
  );
};

AnyfeedITCL.displayName = 'AnyfeedITCL';

AnyfeedITCL.defaultProps = {
  isHeader: false
};

export default AnyfeedITCL;
